import { z } from 'zod';
import { EVENT_MODULE_KEYS } from '../types/eventModules.js';

const isoDateSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/).refine((value) => {
  const [year, month, day] = value.split('-').map(Number);
  const date = new Date(Date.UTC(year, month - 1, day));
  return date.getUTCFullYear() === year
    && date.getUTCMonth() === month - 1
    && date.getUTCDate() === day;
}, 'Invalid calendar date');

const moduleKeySchema = z.enum(EVENT_MODULE_KEYS);
const moduleOverrideSchema = z.object({
  enabled: z.boolean().nullable().optional(),
  label: z.string().max(40).nullable().optional(),
  order: z.number().int().min(0).max(1000).nullable().optional(),
  filters: z.array(z.object({
    type: z.string().min(1).max(40),
    label: z.string().max(40).nullable().optional(),
    enabled: z.boolean().nullable().optional(),
    order: z.number().int().min(0).max(1000).nullable().optional(),
  }).strict()).max(32).nullable().optional(),
  paper_ai: z.boolean().nullable().optional(),
  session_prep_types: z.array(z.string()).max(32).nullable().optional(),
}).strict();

const homeBlockSchema = z.object({
  enabled: z.boolean().nullable().optional(),
  title: z.string().max(80).nullable().optional(),
  order: z.number().int().min(0).max(1000).nullable().optional(),
  visibility: z.array(z.enum(['before', 'during', 'after'])).min(1).max(3).nullable().optional(),
  module_keys: z.array(moduleKeySchema).max(3).nullable().optional(),
  limit: z.number().int().min(1).max(10).nullable().optional(),
}).strict();

const customActionSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('internal_module'), label: z.string().min(1).max(40), module_key: moduleKeySchema }).strict(),
  z.object({ kind: z.literal('external_url'), label: z.string().min(1).max(40), url: z.string().url() }).strict(),
]);

const eventHomeLayoutSchema = z.object({
  version: z.literal(1).optional(),
  blocks: z.record(z.string(), homeBlockSchema).optional(),
  custom_sections: z.array(z.object({
    id: z.string().min(1).max(64),
    title: z.string().min(1).max(80),
    body_markdown: z.string().min(1).max(4000),
    enabled: z.boolean(),
    order: z.number().int().min(0).max(1000),
    visibility: z.array(z.enum(['before', 'during', 'after'])).min(1).max(3),
    image_url: z.string().nullable().optional(),
    image_alt: z.string().max(160).nullable().optional(),
    action: customActionSchema.nullable().optional(),
    content_style: z.enum(['formatted_text', 'featured_sponsors']).optional(),
  }).strict()).max(20).optional(),
}).strict();

export const eventBuilderPreviewPayloadSchema = z.object({
  version: z.literal(1),
  revision: z.number().int().min(0),
  requirements: z.object({
    name: z.string().nullable(),
    date_start: isoDateSchema.nullable(),
    date_end: isoDateSchema.nullable(),
    timezone: z.string().nullable(),
    location: z.string().nullable(),
    online: z.boolean().nullable(),
    access: z.enum(['invite_only', 'public']).nullable(),
  }).strict(),
  blueprint: z.object({
    app_modules: z.record(moduleKeySchema, moduleOverrideSchema),
    home_layout: eventHomeLayoutSchema,
  }).strict(),
}).strict();

export type ValidatedEventBuilderPreviewPayload = z.infer<typeof eventBuilderPreviewPayloadSchema>;
